import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';
import { QuestionResponseDto, QuestionResponseTextDto } from 'models/dtoTypes';
import Box from '@mui/material/Box';
import SmeConfigContext from 'data/SmeConfigContext';
import SmeConfig from 'data/SmeConfig';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import LoadingButton from '@mui/lab/LoadingButton';
import { TextField, Button } from '@mui/material';
import { GetSection, CreateOrUpdateResponse } from 'services/section';
import { date2str } from 'utils/date';
import { useTranslation } from 'react-i18next';

const PREFIX = 'ConfirmAndSubmitPage';

const classes = {
  content: `${PREFIX}-content`,
  boxContainer: `${PREFIX}-boxContainer`,
};

const StyledConfirmAndSubmitPage = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,

  [`& .${classes.content}`]: {
    display: 'flex',
    flexGrow: 1,
    backgroundColor: '#e7ebef',
  },

  [`& .${classes.boxContainer}`]: {
    flex: 1,
    borderRadius: 10,
    padding: 30,
    width: '100%',
    height: '100%',
    backgroundColor: 'white',
  },
}));

function ConfirmAndSubmitPage() {
  const alias = 'confirm-and-submit';
  const nameAlias = 'signed-by-name';
  const dateAlias = 'signed-at';
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const { t } = useTranslation('default');

  // get the current section schema
  const fetchData = async () => {
    const response = await GetSection(alias);
    const data = response?.data;
    if (data) {
      const responses = data?.sectionResponse?.responses || [];
      const nameResponse = responses.find((r) => r.questionAlias === nameAlias);
      const dateResponse = responses.find((r) => r.questionAlias === dateAlias);
      if (nameResponse || dateResponse) {
        setName(String(get(nameResponse, 'value') || ''));
        setName(String(get(dateResponse, 'value') || ''));
      } else {
        // no historical responses
        setIsEdit(true);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchData();
  }, []);

  const renderText = (para: string, index: number) => {
    return <p key={index}>{para}</p>;
  };

  async function handleSubmit() {
    const questionResponses: QuestionResponseTextDto[] = [
      {
        questionAlias: nameAlias,
        value: name,
        type: 'QuestionResponseTextDto',
      },
      {
        questionAlias: dateAlias,
        value: date2str(new Date()),
        type: 'QuestionResponseDateDto',
      },
    ];
    setSubmitting(true);
    const response = await CreateOrUpdateResponse(
      {
        alias,
        updateScore: true,
      },
      {
        sectionAlias: alias,
        questionResponses,
        subSectionResponses: [],
      },
    );

    if (response?.status === 200) {
      console.log('yeah');
      const responses = (get(response.data, 'responses') || []) as QuestionResponseDto[];
      const nameResponse = responses.find((r) => r.questionAlias === nameAlias);
      const dateResponse = responses.find((r) => r.questionAlias === dateAlias);
      if (nameResponse || dateResponse) {
        setName(get(nameResponse, 'value') || '');
        setDate(get(dateResponse, 'value') || '');
      }
      setIsEdit(false);
    }
    setSubmitting(false);
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const renderButton = () => {
    if (isEdit) {
      return (
        <LoadingButton
          loading={submitting}
          variant="contained"
          size="large"
          color="primary"
          onClick={handleSubmit}
        >
          {t('i-agree')}
        </LoadingButton>
      );
    } else {
      return (
        <Button
          size="large"
          type="submit"
          variant="contained"
          color="primary"
          aria-label="Signup"
          onClick={() => setIsEdit(true)}
        >
          {t('re-sign')}
        </Button>
      );
    }
  };

  const renderForm = (config: SmeConfig) => {
    console.log(config);
    const defaultDate = isEdit ? new Date() : new Date(date);
    return (
      <div>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              sx={{ width: '100%' }}
              required
              id="name"
              label={t('name')}
              color="primary"
              value={name}
              disabled={!isEdit || submitting}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              sx={{ width: '100%' }}
              required
              id="Date"
              label={t('date')}
              color="primary"
              value={date2str(defaultDate)}
              disabled={true}
            />
          </Grid>
        </Grid>
        <Box sx={{ m: 3, textAlign: 'center' }}>{renderButton()}</Box>
      </div>
    );
  };

  const renderConfirmPage = (config: SmeConfig) => {
    const params = [
      'Your Company Profile is determined based on the information you provide, under the following terms:',
      '',
      'You are required to exercise due diligence in ensuring that all responses are complete and truthful. Following the assignment of a company registration profile, it is imperative to promptly update your profile should any of the provided information change. Providing outdated or inaccurate information may lead to the invalidation of registration statuses and may impact compliance reports.',
      '',
      // eslint-disable-next-line
      "All registrants are obligated to represent their business data accurately, as this information forms the basis for the company evaluation. This entails disclosing any details that could potentially affect the algorithm's assessment of your registration profile. If there is any doubt regarding the materiality of information, err on the side of disclosure wherever feasible. Prior to submitting information into the system, you must conduct a thorough investigation to ascertain all necessary disclosures. Omitting material information could invalidate registration profiles and adversely affect compliance reports.",
      '',
      'Every statement, fact, and document submitted through the registration process must be complete, truthful, and accurate. If the forms are completed by someone else on your behalf, you must review them for accuracy and completeness prior to submission. It is essential to thoroughly read and understand the declaration before agreeing to it.',
      '',
      'I hereby declare that all information provided is accurate and truthful to the best of my knowledge and belief, and I commit to immediately updating the company profile regarding any changes therein.',
    ];
    // TODO: should disable submit unless all other sectiosn are completed
    console.log(
      JSON.stringify(
        config.sections.map((x) => x.alias),
        null,
        2,
      ),
    );
    return (
      <StyledConfirmAndSubmitPage>
        <div className={classes.content}>
          <Box p={15} className={classes.boxContainer}>
            <h3>{t('terms-and-conditions')}</h3>
            <h4>{t('providing-smart-governance-information')}</h4>
            {params.map(renderText)}
            {renderForm(config)}
          </Box>
        </div>
      </StyledConfirmAndSubmitPage>
    );
  };

  if (loading) {
    return (
      <div style={{ margin: 20 }}>
        <CircularProgress />
      </div>
    );
  }
  return <SmeConfigContext.Consumer>{renderConfirmPage}</SmeConfigContext.Consumer>;
}

export default ConfirmAndSubmitPage;
